/* ==========================================================================
   <sura-scroller> — arrows + edge state for any scroll-snap track.
   Reused by the bestsellers carousel, latest releases and exam tiles.
   ========================================================================== */

class SuraScroller extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-scroller-track]');
    this.prevBtn = this.querySelector('[data-scroller-prev]');
    this.nextBtn = this.querySelector('[data-scroller-next]');

    if (!this.track) return;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.page(-1));
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.page(1));

    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    // Card widths change with the viewport, so recompute on resize.
    this.resizeObserver = new ResizeObserver(() => this.updateState());
    this.resizeObserver.observe(this.track);

    this.updateState();
  }

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.track) this.track.removeEventListener('scroll', this.onScroll);
  }

  onScroll = () => {
    // Coalesce scroll events into one paint.
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.updateState();
    });
  };

  /* Distance of one "page": as many whole cards as currently fit. */
  get step() {
    const item = this.track.querySelector('[data-scroller-item]');
    if (!item) return this.track.clientWidth;

    const styles = getComputedStyle(this.track);
    const gap = parseFloat(styles.columnGap || styles.gap) || 0;
    const itemWidth = item.getBoundingClientRect().width + gap;
    const perPage = Math.max(1, Math.floor(this.track.clientWidth / itemWidth));

    return itemWidth * perPage;
  }

  page(direction) {
    this.track.scrollBy({
      left: this.step * direction,
      behavior: this.reducedMotion ? 'auto' : 'smooth',
    });
  }

  updateState() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    // 2px tolerance: sub-pixel widths mean scrollLeft rarely hits the exact max.
    const atStart = scrollLeft <= 2;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 2;
    const noOverflow = scrollWidth <= clientWidth + 2;

    this.classList.toggle('sura-scroller--static', noOverflow);

    if (this.prevBtn) this.prevBtn.disabled = atStart || noOverflow;
    if (this.nextBtn) this.nextBtn.disabled = atEnd || noOverflow;
  }
}

if (!customElements.get('sura-scroller')) {
  customElements.define('sura-scroller', SuraScroller);
}
